import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Space, Select, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import FeatureItem from './feature-item';
import featureStore from 'common/stores/features/feature.store';
import { observer } from 'mobx-react-lite';
import Feature from 'common/components/feature/feature';
import { FeatureName } from 'common/enums/feature-name.enum';
import { notification } from 'common/utils/notification';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import {
  ISystemConfigurationUpdate,
  ISmartPlateConfigurationUpdate,
  ISystemConfigurationRead,
  SmartPlateAlgorithmType,
  ICitationConfigurationUpdate,
  IPayEaseConfigurationUpdate,
} from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import cardAccessService from 'common/services/card-access-service/card-access.service';
import styles from './features.module.scss';
import { FormInstance } from 'antd/lib';
import dayjs, { Dayjs } from 'dayjs';
import { trackPromise } from 'react-promise-tracker';

const Features = observer(() => {
  const citationInitData = configurationStore.configuration?.citations;
  const cardAccessInitData = configurationStore.configuration?.cardAccessConfiguration;
  const smartPlateInitData = configurationStore.configuration?.smartPlate;
  const payEaseInitData = configurationStore.configuration?.payEase;

  const algorithmOptions = [
    { value: SmartPlateAlgorithmType.W26S, label: 'Wiegand 26-bit' },
    { value: SmartPlateAlgorithmType.W34N, label: 'Wiegand 34-bit' },
    { value: SmartPlateAlgorithmType.W37S, label: 'Wiegand 37-bit' },
  ];

  const eiqIntelliTicketFeature = featureStore.getByName(FeatureName.EIQIntelliTicket);

  const [citationForm] = Form.useForm();
  const [cardAccessForm] = Form.useForm();
  const [smartPlateForm] = Form.useForm();
  const [payEaseForm] = Form.useForm();

  const [regenerateDisabled, setRegenerateDisabled] = useState(true);
  const [regenerateDate, setRegenerateDate] = useState<Dayjs | null>(null);

  let timeoutId: any;

  const handleFormValuesChange = (form: FormInstance<any>) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      form.submit();
    }, 1000);
  };

  const updateConfiguration = async (model: any) => {
    const result = await configurationService.updateSystemConfiguration(model);
    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  const setRegenerateButtonStatus = (configuration: ISystemConfigurationRead | null) => {
    const lastRegeneration = configuration?.smartPlate?.lastRegeneration;

    if (lastRegeneration) {
      const parsed = dayjs.utc(lastRegeneration);
      setRegenerateDate(parsed);

      if (parsed.local().add(1, 'hour').isAfter(dayjs())) {
        setRegenerateDisabled(true);
        return;
      }
    }

    setRegenerateDisabled(false);
  };

  const onChangeCardAccessConfig = async (values: any) => {
    const request = {
      cardAccessConfiguration: {
        logImportIntervalInMinutes: null,
      },
    };
    const model = request as ISystemConfigurationUpdate;
    model.cardAccessConfiguration!.logImportIntervalInMinutes = Number(values.logImportIntervalInMinutes);
    await updateConfiguration(model);
  };

  const onChangeEIQIntelliticketConfig = async (values: any) => {
    const request = {
      citations: {
        citationPaymentCommissionInPercentages: null,
      },
    };
    const model = request as ICitationConfigurationUpdate;
    model.citations!.citationPaymentCommissionInPercentages = Number(values.citationPaymentCommissionInPercentages);
    await updateConfiguration(model);
  };

  const onChangePayEaseConfig = async (values: any) => {
    const model: IPayEaseConfigurationUpdate = {
      payEase: {
        pinCode: values.pinCode ?? null,
        eiqCommissionInPercentages:
          values.eiqCommissionInPercentages === undefined ||
          values.eiqCommissionInPercentages === null ||
          values.eiqCommissionInPercentages === ''
            ? null
            : Number(values.eiqCommissionInPercentages),
        terminalReaderId: values.terminalReaderId?.trim() || null,
      },
    };
    await updateConfiguration(model);
  };

  const onSmartPlateConfigChange = async (values: any) => {
    const model: ISmartPlateConfigurationUpdate = {
      smartPlate: {
        algorithmType: values.algorithmType,
        facilityCode: values.facilityCode,
      },
    };
    await updateConfiguration(model);
  };

  const handleRegenerate = async () => {
    setRegenerateDisabled(true);
    setRegenerateDate(dayjs());

    await trackPromise(cardAccessService.generateSmartPlateIds(true));
    notification.success({
      message: 'SmartPlate IDs regeneration and sync started',
      duration: 5,
    });

    const configuration = await configurationService.updateSystemConfiguration({
      smartPlate: { lastRegeneration: new Date().toISOString() },
    } as ISmartPlateConfigurationUpdate);
    setRegenerateButtonStatus(configuration);
    configurationStore.setConfiguration(configuration);
  };

  useEffect(() => {
    if (citationForm) {
      citationForm.setFieldsValue(citationInitData);
    }
  }, [citationForm, citationInitData]);

  useEffect(() => {
    if (payEaseForm) {
      payEaseForm.setFieldsValue(payEaseInitData);
    }
  }, [payEaseForm, payEaseInitData]);

  useEffect(() => {
    if (smartPlateForm && smartPlateInitData) {
      smartPlateForm.setFieldsValue({
        algorithmType: smartPlateInitData.algorithmType,
        facilityCode: smartPlateInitData.facilityCode,
      });
    }
  }, [smartPlateForm, smartPlateInitData]);

  useEffect(() => {
    setRegenerateButtonStatus(configurationStore.configuration);
  }, [smartPlateInitData]);

  return (
    <Card className="eiq-card eiq-card-border eiq-configuration">
      <Row className="eiq-card-header">
        <Col span={24}>
          <h1 className="h1">Feature management</h1>
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={24} md={24}>
          <FeatureItem feature={featureStore.getByName(FeatureName.SmsNotifications)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.MassAlerts)!} />
          {featureStore.isMassAlertsEnabled && (
            <div className={styles.container}>
              <FeatureItem feature={featureStore.getByName(FeatureName.SmsMassAlerts)!} />
            </div>
          )}
          <div className={styles.container}>
            <div className={styles.title}>Napco Security</div>

            <FeatureItem feature={featureStore.getByName(FeatureName.CA3000)!} disabled={featureStore.isCA4000Enabled} />
            {featureStore.isCA3000Enabled && (
              <div className={styles.container}>
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3000CardClasses)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3000Expirations)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3KLogsImport)!} />
                {featureStore.isCardAccessLogsImportEnabled && (
                  <FormWrapper
                    form={cardAccessForm}
                    onFinish={onChangeCardAccessConfig}
                    onValuesChange={() => handleFormValuesChange(cardAccessForm)}
                    initialValues={cardAccessInitData}
                    layout="vertical"
                    className={styles.EIQIntelliTicketConfiguration}>
                    <Space className="full-width" size={13}>
                      <h1 className="h1">Card Access configuration</h1>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item name="logImportIntervalInMinutes" label="Log import interval in minutes">
                        <Input placeholder="Enter interval" type="number" min={10} step={1} />
                      </Form.Item>
                    </Space>
                  </FormWrapper>
                )}
                <FeatureItem feature={featureStore.getByName(FeatureName.SmartPlateId)!} />
                {featureStore.isSmartPlateIdEnabled && (
                  <FormWrapper
                    form={smartPlateForm}
                    onFinish={onSmartPlateConfigChange}
                    onValuesChange={() => handleFormValuesChange(smartPlateForm)}
                    initialValues={smartPlateInitData}
                    layout="vertical"
                    className={styles.EIQIntelliTicketConfiguration}>
                    <Space className="full-width" size={13}>
                      <h1 className="h1">Smart Plate Configuration</h1>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item
                        name="algorithmType"
                        label="Smart Plate Algorithm"
                        rules={[{ required: true, message: 'Please select an algorithm type' }]}>
                        <Select placeholder="Select algorithm type" options={algorithmOptions} />
                      </Form.Item>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item
                        name="facilityCode"
                        label="Facility Code"
                        rules={[{ required: true, message: 'Please enter the facility code' }]}>
                        <Input placeholder="Enter facility code" type="number" min={0} step={1} />
                      </Form.Item>
                    </Space>
                    <div className={styles.regenerateBtn}>
                      <Tooltip
                        title={
                          regenerateDisabled && regenerateDate
                            ? `Will be available at ${regenerateDate.local().add(1, 'hour').format('MM/DD/YYYY hh:mm A')}`
                            : ''
                        }>
                        <span>
                          <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            style={{ width: '100%' }}
                            onClick={handleRegenerate}
                            disabled={regenerateDisabled}>
                            Regenerate & Sync
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </FormWrapper>
                )}
              </div>
            )}

            <FeatureItem feature={featureStore.getByName(FeatureName.CA4K)!} disabled={featureStore.isCA3000Enabled} />
            {featureStore.isCA4000Enabled && (
              <div className={styles.container}>
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3000CardClasses)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3000Expirations)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.CA3KLogsImport)!} />
                {featureStore.isCardAccessLogsImportEnabled && (
                  <FormWrapper
                    form={cardAccessForm}
                    onFinish={onChangeCardAccessConfig}
                    onValuesChange={() => handleFormValuesChange(cardAccessForm)}
                    initialValues={cardAccessInitData}
                    layout="vertical"
                    className={styles.EIQIntelliTicketConfiguration}>
                    <Space className="full-width" size={13}>
                      <h1 className="h1">Card Access configuration</h1>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item name="logImportIntervalInMinutes" label="Log import interval in minutes">
                        <Input placeholder="Enter interval" type="number" min={10} step={1} />
                      </Form.Item>
                    </Space>
                  </FormWrapper>
                )}
                <FeatureItem feature={featureStore.getByName(FeatureName.SmartPlateId)!} />
                {featureStore.isSmartPlateIdEnabled && (
                  <FormWrapper
                    form={smartPlateForm}
                    onFinish={onSmartPlateConfigChange}
                    onValuesChange={() => handleFormValuesChange(smartPlateForm)}
                    initialValues={smartPlateInitData}
                    layout="vertical"
                    className={styles.EIQIntelliTicketConfiguration}>
                    <Space className="full-width" size={13}>
                      <h1 className="h1">Smart Plate Configuration</h1>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item
                        name="algorithmType"
                        label="Smart Plate Algorithm"
                        rules={[{ required: true, message: 'Please select an algorithm type' }]}>
                        <Select placeholder="Select algorithm type" options={algorithmOptions} />
                      </Form.Item>
                    </Space>
                    <Space className="full-width" size={13}>
                      <Form.Item
                        name="facilityCode"
                        label="Facility Code"
                        rules={[{ required: true, message: 'Please enter the facility code' }]}>
                        <Input placeholder="Enter facility code" type="number" min={0} step={1} />
                      </Form.Item>
                    </Space>
                    <div className={styles.regenerateBtn}>
                      <Tooltip
                        title={
                          regenerateDisabled && regenerateDate
                            ? `Will be available at ${regenerateDate.local().add(1, 'hour').format('MM/DD/YYYY hh:mm A')}`
                            : ''
                        }>
                        <span>
                          <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            style={{ width: '100%' }}
                            onClick={handleRegenerate}
                            disabled={regenerateDisabled}>
                            Regenerate & Sync
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </FormWrapper>
                )}
                <FeatureItem feature={featureStore.getByName(FeatureName.GateController)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.EIQLive)!} />
              </div>
            )}
          </div>
          <FeatureItem feature={featureStore.getByName(FeatureName.Insight)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.Connect)!} />
          <div className={styles.container}>
            <FeatureItem feature={featureStore.getByName(FeatureName.ConnectEditPin)!} />
            <FeatureItem feature={featureStore.getByName(FeatureName.FastAccess)!} />
            {featureStore.isFastAccessEnabled && (
              <div className={styles.container}>
                <FeatureItem feature={featureStore.getByName(FeatureName.FastAccessAlerts)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.FastAccessRestrictions)!} />
                <FeatureItem feature={featureStore.getByName(FeatureName.FastAccessPrintDirection)!} />
              </div>
            )}
            <FeatureItem feature={featureStore.getByName(FeatureName.ConnectFID)!} />
          </div>
          <FeatureItem feature={featureStore.getByName(FeatureName.NorthStar)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.Jonas)!} />
          {featureStore.isJonasEnabled && (
            <div className={styles.container}>
              <FeatureItem feature={featureStore.getByName(FeatureName.JonasSearch)!} />
            </div>
          )}
          <FeatureItem feature={featureStore.getByName(FeatureName.CreateResidentAnonymously)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.LPR)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.EIQWhoIs)!} />

          <FeatureItem feature={featureStore.getByName(FeatureName.GuestLimitations)!} />
          {featureStore.isGuestLimitationsEnabled && (
            <div className={styles.container}>
              <FeatureItem feature={featureStore.getByName(FeatureName.GuestLimitationsForAdmins)!} />
            </div>
          )}
          <FeatureItem feature={featureStore.getByName(FeatureName.BasIP)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.DriveId)!} />
          <div className={styles.container}>
            <div className={styles.title}>Traffic Logix</div>
            <FeatureItem feature={eiqIntelliTicketFeature!} />
            <Feature name={FeatureName.EIQIntelliTicket}>
              <FormWrapper
                form={citationForm}
                onFinish={onChangeEIQIntelliticketConfig}
                onValuesChange={() => handleFormValuesChange(citationForm)}
                initialValues={citationInitData}
                layout="vertical"
                className={styles.EIQIntelliTicketConfiguration}>
                <Space className="full-width" size={13}>
                  <h1 className="h1">EIQ-IntelliTicket configuration</h1>
                </Space>
                <Space className="full-width" size={13}>
                  <Form.Item name="citationPaymentCommissionInPercentages" label="EIQ payment commission (%)">
                    <Input placeholder="Enter commission amount" type="number" min={0} step={0.1} />
                  </Form.Item>
                </Space>
              </FormWrapper>
            </Feature>
          </div>
          <FeatureItem feature={featureStore.getByName(FeatureName.IncidentReporter)!} />
          <div className={styles.container}>
            {featureStore.isIncidentReporterEnabled && <FeatureItem feature={featureStore.getByName(FeatureName.DailyActivityReports)!} />}
          </div>
          <FeatureItem feature={featureStore.getByName(FeatureName.EIQProfile)!} />
          <FeatureItem feature={featureStore.getByName(FeatureName.PayEase)!} />
          <Feature name={FeatureName.PayEase}>
            <FormWrapper
              form={payEaseForm}
              onFinish={onChangePayEaseConfig}
              onValuesChange={() => handleFormValuesChange(payEaseForm)}
              initialValues={payEaseInitData}
              layout="vertical"
              className={styles.EIQIntelliTicketConfiguration}>
              <Space className="full-width" size={13}>
                <h1 className="h1">EIQ-PayEase configuration</h1>
              </Space>
              <Space className="full-width" size={13}>
                <Form.Item
                  name="pinCode"
                  label="PIN code"
                  rules={[
                    { required: true, message: 'Please enter a PIN code' },
                    { pattern: /^\d{4,6}$/, message: 'PIN code must be 4 to 6 digits' },
                  ]}>
                  <Input placeholder="Enter PIN code" inputMode="numeric" maxLength={6} />
                </Form.Item>
              </Space>
              <Space className="full-width" size={13}>
                <Form.Item name="eiqCommissionInPercentages" label="EIQ commission (%)">
                  <Input placeholder="Enter commission amount" type="number" min={0} step={0.1} />
                </Form.Item>
              </Space>
              <Space className="full-width" size={13}>
                <Form.Item name="terminalReaderId" label="Stripe terminal reader ID">
                  <Input placeholder="tmr_..." />
                </Form.Item>
              </Space>
            </FormWrapper>
          </Feature>
        </Col>
      </Row>
    </Card>
  );
});

export default Features;
